import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { AiOutlineClose } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import OnboardingService from "../../services/onboarding.service";
import {
  shouldShowAppWalkthrough,
  markWalkthroughSeenLocal,
} from "../../Util/onboarding";
import { setWalkthroughSeen, toggleSideBar, setSideBarCollapsed } from "../../store/redux/User/actions";
import { roleOf } from "../../Util/session";
import { stepsFor, needsMenu, menuCanOpen } from "./steps";
import "./AppWalkthrough.scss";

// Padding around the spotlight ring, in pixels.
const SPOTLIGHT_PAD = 10;
// Gap between the spotlight edge and the popover.
const POPOVER_GAP = 16;
// How long to keep looking for a step's target (ms). When the time is up the step is SKIPPED,
// in the direction the user was going (VT review 7/8). It used to stay on screen saying
// "Couldn't find this element on the current page", which told a new agent nothing; the tour
// now opens what a step needs, so a target that is still missing is one this user does not
// have (a header link on a narrow screen), not a step worth showing.
// The wait is short, because nothing is drawn while it lasts: a control of the page that is
// there is there at once. Only a step that had to open something waits longer: the menu
// (it opens in the same breath) and another page (it has to arrive).
const TARGET_WAIT_TIMEOUT = 700;
const TARGET_WAIT_FOR_MENU = 2000;
const TARGET_WAIT_FOR_PAGE = 5000;
// Re-measure cadence while a step is active.
const MEASURE_INTERVAL = 250;

// Map a step's preferred placement to a CSS class. Real placement may flip if
// the popover wouldn't fit in the viewport.
const PLACEMENT_CLASS = {
  right: "is-right",
  left: "is-left",
  bottom: "is-bottom",
  top: "is-top",
  center: "is-center",
};

const AppWalkthrough = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  // Subscribe to the onboarding-complete signal so we re-evaluate the gate
  // the moment the chromeless flow finishes and the user lands in the app.
  // (Initial mount happens with active=false for first-time agents — they're
  // still in the chromeless shell — and flips when redux says completed.)
  const onboardingCompletedFlag = useSelector(
    (state) => state.user.settings?.onboarding?.completed
  );
  const walkthroughSeenFlag = useSelector(
    (state) => state.user.settings?.onboarding?.walkthroughSeen
  );
  // Sidebar collapsed state — when we enter a sidebar-* step we dispatch
  // toggleSideBar(true) if it's closed, so the spotlight has something to
  // anchor on. Sidebar/index.js only renders items when activeMenu (driven
  // by this redux flag) is true on wide screens.
  const sideBarOpen = useSelector((state) => state.user.settings?.SideBar);
  // The menu narrowed to icons: a menu step widens it first, so the item can be read.
  const sideBarCollapsed = useSelector((state) => Boolean(state.user.settings?.SideBarCollapsed));

  const [active, setActive] = useState(() => shouldShowAppWalkthrough());
  const [stepIndex, setStepIndex] = useState(0);
  // The steps THIS user is shown: an agent's tour has no admin-only item (./steps.js).
  // On a screen where the left menu cannot open (a phone) its steps are left out too.
  const steps = useMemo(
    () => stepsFor(roleOf(), { menu: menuCanOpen(typeof window === "undefined" ? undefined : window.innerWidth) }),
    [active] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Where the tour started (page, and the menu's width), to put both back when it ends.
  const startRef = useRef(null);
  // 1 = the user pressed Next, -1 = Back: the way a step with nothing to point at is skipped.
  const directionRef = useRef(1);

  // Re-check the gate whenever redux says onboarding completed or the
  // walkthrough was seen. shouldShowAppWalkthrough() also reads localStorage
  // so this stays consistent if redux is reset by a page refresh.
  useEffect(() => {
    if (walkthroughSeenFlag) {
      setActive(false);
      return;
    }
    setActive(shouldShowAppWalkthrough());
  }, [onboardingCompletedFlag, walkthroughSeenFlag]);

  useEffect(() => {
    if (active && !startRef.current) {
      startRef.current = { path: `${location.pathname}${location.search}`, collapsed: sideBarCollapsed };
    }
    if (!active) startRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Open what the step points at, once per step: the page that holds its control (a search
  // step started from Reservations has no search bar to point at), and the left menu, shown
  // and wide, for a menu item. On screens < 800px the layout keeps the menu closed
  // regardless; such a step then finds nothing and is skipped.
  const stepIdRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const current = steps[stepIndex];
    const id = current?.id;
    if (!id || stepIdRef.current === id) return;
    stepIdRef.current = id;
    if (current.route && location.pathname !== current.route) {
      history.push(current.route);
    }
    if (needsMenu(current)) {
      if (!sideBarOpen) dispatch(toggleSideBar(true));
      if (sideBarCollapsed) dispatch(setSideBarCollapsed(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIndex, steps, sideBarOpen, sideBarCollapsed, dispatch]);
  // Geometry of the current target relative to the viewport.
  const [rect, setRect] = useState(null);
  const [placement, setPlacement] = useState("right");
  const [finishing, setFinishing] = useState(false);

  const intervalRef = useRef(null);
  const waitTimerRef = useRef(null);
  const skipMissingStepRef = useRef(() => {});

  const step = steps[stepIndex];
  const total = steps.length;

  // Recompute on demand. Returns whether a target was found.
  const measure = useCallback(() => {
    if (!step) return false;
    if (!step.selector) {
      setRect(null);
      setPlacement("center");
      return true;
    }
    const el = document.querySelector(step.selector);
    if (!el) {
      setRect(null);
      return false;
    }
    const r = el.getBoundingClientRect();
    // Filter out zero-area elements (display:none, collapsed).
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return false;
    }
    setRect({
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
      bottom: r.bottom,
      right: r.right,
    });
    // Decide placement: respect step.placement when it fits, otherwise flip.
    const preferred = step.placement || "right";
    const popoverEstW = 340;
    const popoverEstH = 220;
    const fits = {
      right: r.right + POPOVER_GAP + popoverEstW < window.innerWidth,
      left: r.left - POPOVER_GAP - popoverEstW > 0,
      bottom: r.bottom + POPOVER_GAP + popoverEstH < window.innerHeight,
      top: r.top - POPOVER_GAP - popoverEstH > 0,
    };
    if (fits[preferred]) {
      setPlacement(preferred);
    } else {
      const fallback = ["bottom", "right", "left", "top"].find((p) => fits[p]);
      setPlacement(fallback || preferred);
    }
    return true;
  }, [step]);

  // Wait for the target to mount, then start periodic measurement so the
  // ring follows layout shifts (window resize, sidebar collapse, route paint).
  //
  // If the target never appears the step is skipped the way the user was going (see
  // TARGET_WAIT_TIMEOUT above). The popover is not drawn while the target is looked for.
  useLayoutEffect(() => {
    if (!active || !step) return undefined;

    const tryMeasure = () => measure();

    if (!tryMeasure()) {
      const startedAt = Date.now();
      // Read as the step begins: is its control on another page (the tour is on its way there)?
      const pageOnItsWay = Boolean(step.route) && location.pathname !== step.route;
      const patience = pageOnItsWay
        ? TARGET_WAIT_FOR_PAGE
        : needsMenu(step) ? TARGET_WAIT_FOR_MENU : TARGET_WAIT_TIMEOUT;
      const waitId = setInterval(() => {
        if (tryMeasure()) {
          clearInterval(waitId);
          waitTimerRef.current = null;
        } else if (Date.now() - startedAt > patience) {
          clearInterval(waitId);
          waitTimerRef.current = null;
          // eslint-disable-next-line no-console
          console.warn(
            `[AppWalkthrough] step "${step.id}": ${step.selector} matched no element; step skipped`
          );
          skipMissingStepRef.current();
        }
      }, 100);
      waitTimerRef.current = waitId;
    }

    intervalRef.current = setInterval(tryMeasure, MEASURE_INTERVAL);
    const onResize = () => tryMeasure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      clearInterval(intervalRef.current);
      if (waitTimerRef.current) clearInterval(waitTimerRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stepIndex, measure]);

  // Scroll the target into view if it's offscreen.
  useEffect(() => {
    if (!rect) return;
    const offTop = rect.top < 0;
    const offBottom = rect.bottom > window.innerHeight;
    if (offTop || offBottom) {
      const el = document.querySelector(step?.selector || "");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [rect, step]);

  const finish = async ({ skipped = false } = {}) => {
    if (finishing) return;
    setFinishing(true);
    // Read now: marking the tour as seen (below) closes it at once, and the start is forgotten
    // with it, before the request has come back.
    const start = startRef.current;
    // Mark locally first so a network failure doesn't make the tour re-fire
    // on the next render.
    markWalkthroughSeenLocal();
    dispatch(setWalkthroughSeen());
    try {
      await OnboardingService.completeWalkthrough({ skipped });
    } catch (e) {
      // Local flag already set — silent fail is acceptable.
      console.warn("completeWalkthrough failed:", e);
    } finally {
      // Leave things as the tour found them: the menu's width and the page it started on.
      if (start) {
        if (start.collapsed) dispatch(setSideBarCollapsed(true));
        if (start.path && start.path !== `${location.pathname}${location.search}`) history.push(start.path);
      }
      setActive(false);
      setFinishing(false);
    }
  };

  // `silent` skip is used internally when a target never appears — we don't
  // want auto-skips to mark the whole tour as user-skipped.
  const goNext = ({ silent = false } = {}) => {
    directionRef.current = 1;
    if (stepIndex + 1 >= total) {
      finish({ skipped: false });
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const goBack = () => {
    directionRef.current = -1;
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  // A step with nothing to point at: move on the way the user was going. Going back past the
  // first step is not possible, so from there it goes forward. Read through a ref by the
  // waiting timer above, which is set up before this is defined.
  skipMissingStepRef.current = () => {
    if (directionRef.current === -1 && stepIndex > 0) {
      setStepIndex((i) => Math.max(i - 1, 0));
    } else {
      goNext({ silent: true });
    }
  };

  if (!active || !step) return null;
  // Render the popover when we have a measured rect or an intentionally-centered step.
  // While a target is still looked for (a page or the menu is opening) nothing is drawn;
  // past the wait the step is skipped.
  const showPopover = !!rect || (!step.selector && placement === "center");
  const effectivePlacement = placement;

  // Spotlight position with padding.
  const spotlight = rect
    ? {
        top: rect.top - SPOTLIGHT_PAD,
        left: rect.left - SPOTLIGHT_PAD,
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
      }
    : null;

  // Popover anchor position derived from spotlight + placement.
  // We compute raw top/left (no transforms) so we can clamp them to the
  // viewport — otherwise a target near the right/bottom edge produces a
  // popover whose Next button sits outside the visible area.
  const popoverStyle = (() => {
    if (!rect || effectivePlacement === "center") {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }

    // Estimated popover footprint. Real width is clamped by CSS (max-width:
    // calc(100vw - 32px)) so the actual element won't exceed this.
    const popW = 340;
    const popH = 220;
    const VIEWPORT_MARGIN = 16;
    const maxLeft = window.innerWidth - popW - VIEWPORT_MARGIN;
    const maxTop = window.innerHeight - popH - VIEWPORT_MARGIN;

    let top;
    let left;
    switch (effectivePlacement) {
      case "right":
        top = rect.top + rect.height / 2 - popH / 2;
        left = rect.right + POPOVER_GAP;
        break;
      case "left":
        top = rect.top + rect.height / 2 - popH / 2;
        left = rect.left - POPOVER_GAP - popW;
        break;
      case "bottom":
        top = rect.bottom + POPOVER_GAP;
        left = rect.left + rect.width / 2 - popW / 2;
        break;
      case "top":
        top = rect.top - POPOVER_GAP - popH;
        left = rect.left + rect.width / 2 - popW / 2;
        break;
      default:
        top = rect.bottom + POPOVER_GAP;
        left = rect.left;
    }

    // Clamp to viewport so the popover (and especially the Next button on the
    // right side) is always fully reachable.
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, maxTop));

    return { top, left };
  })();

  return (
    <div className="app-walkthrough" role="dialog" aria-modal="true">
      {/* SVG mask = solid backdrop with a rounded cutout around the target. */}
      <svg className="app-walkthrough__backdrop" aria-hidden="true">
        <defs>
          <mask id="app-walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.left}
                y={spotlight.top}
                width={spotlight.width}
                height={spotlight.height}
                rx="10"
                ry="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(15, 31, 50, 0.65)"
          mask="url(#app-walkthrough-mask)"
        />
      </svg>

      {spotlight && (
        <motion.div
          className="app-walkthrough__ring"
          initial={false}
          animate={spotlight}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}

      {/* The card fades IN and is simply taken away: no fade-out, and no AnimatePresence. With
          <AnimatePresence mode="wait"> the next card was held back until the last one had
          faded out. A card that was taken away again before it was ever drawn (a step whose
          target is not there yet: the search page still arriving) left that queue stuck, and
          the step and every step after it showed the ring and the toolbar but no card. */}
      <>
        {showPopover && (
          <motion.div
            key={stepIndex}
            className={`app-walkthrough__popover ${PLACEMENT_CLASS[effectivePlacement] || ""}`}
            style={popoverStyle}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.18 }}
          >
            <div className="app-walkthrough__header">
              <span className="app-walkthrough__counter">
                Step {stepIndex + 1} of {total}
              </span>
              <button
                className="app-walkthrough__close"
                onClick={() => finish({ skipped: true })}
                aria-label="Skip walkthrough"
                disabled={finishing}
              >
                <AiOutlineClose size={16} />
              </button>
            </div>
            <h3 className="app-walkthrough__title">{step.title}</h3>
            <p className="app-walkthrough__body">{step.body}</p>
            <div className="app-walkthrough__progress">
              {steps.map((s, i) => (
                <span
                  key={s.id}
                  className={`app-walkthrough__dot ${i === stepIndex ? "is-active" : ""} ${
                    i < stepIndex ? "is-done" : ""
                  }`}
                />
              ))}
            </div>

            <div className="app-walkthrough__actions">
              <button
                className="app-walkthrough__btn app-walkthrough__btn--ghost"
                onClick={() => finish({ skipped: true })}
                disabled={finishing}
              >
                Skip
              </button>
              <div className="app-walkthrough__nav">
                {stepIndex > 0 && (
                  <button
                    className="app-walkthrough__btn app-walkthrough__btn--secondary"
                    onClick={goBack}
                    disabled={finishing}
                  >
                    Back
                  </button>
                )}
                <button
                  className="app-walkthrough__btn app-walkthrough__btn--primary"
                  onClick={() => goNext()}
                  disabled={finishing}
                >
                  {stepIndex + 1 === total ? (finishing ? "Saving…" : "Got it") : "Next"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </>

      {/* Always-visible fallback toolbar. Pinned to the bottom of the viewport
          so Back/Next/Skip stay reachable even when the main popover ends up
          off-screen, hidden behind a fixed element, or otherwise unclickable.
          Shows the same step counter for orientation. */}
      <div className="app-walkthrough__floater">
        <span className="app-walkthrough__floater-counter">
          {stepIndex + 1} / {total}
        </span>
        {stepIndex > 0 && (
          <button
            className="app-walkthrough__btn app-walkthrough__btn--secondary"
            onClick={goBack}
            disabled={finishing}
          >
            Back
          </button>
        )}
        <button
          className="app-walkthrough__btn app-walkthrough__btn--primary"
          onClick={() => goNext()}
          disabled={finishing}
        >
          {stepIndex + 1 === total ? (finishing ? "Saving…" : "Got it") : "Next"}
        </button>
        <button
          className="app-walkthrough__btn app-walkthrough__btn--ghost"
          onClick={() => finish({ skipped: true })}
          disabled={finishing}
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default AppWalkthrough;
